import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Collapse,
	IconButton,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth } from "../../contexts/AuthContext";
import { ROLES } from "../../config/supabase";
import VisitTypeMessageModal from "../schools/VisitTypeMessageModal";
import { getActiveVisitTypes, stripHtml } from "../../utils/serviceWindow";

const ANNOUNCEMENT_ROLES = [ROLES.FUNMATH_ADMIN, ROLES.FUNMIYO_EMPLOYEE];

const ServiceWindowAnnouncement = () => {
	const { supabase, user } = useAuth();
	const userProfile = user?.profile;
	const [visitTypes, setVisitTypes] = useState([]);
	const [messagesByTypeId, setMessagesByTypeId] = useState({});
	const [dismissedIds, setDismissedIds] = useState([]);
	const [selectedVisitType, setSelectedVisitType] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [copyNotice, setCopyNotice] = useState("");

	const shouldShow = ANNOUNCEMENT_ROLES.includes(userProfile?.role);

	const fetchAnnouncements = useCallback(async () => {
		if (!shouldShow) return;

		try {
			const [{ data: visitTypeData }, { data: messageData }] = await Promise.all([
				supabase.from("visit_types").select("*").eq("status", true),
				supabase.from("visit_type_messages").select("*").eq("is_active", true),
			]);

			setVisitTypes(visitTypeData || []);

			const messageMap = {};
			(messageData || []).forEach((message) => {
				messageMap[message.visit_type_id] = message;
			});
			setMessagesByTypeId(messageMap);
		} catch (error) {
			console.error("Error loading service window announcements:", error);
		}
	}, [shouldShow, supabase]);

	useEffect(() => {
		fetchAnnouncements();
	}, [fetchAnnouncements]);

	const activeAnnouncements = useMemo(() => {
		return getActiveVisitTypes(visitTypes)
			.filter((visitType) => !dismissedIds.includes(visitType.id))
			.map((visitType) => ({
				visitType,
				message: messagesByTypeId[visitType.id] || null,
			}));
	}, [visitTypes, messagesByTypeId, dismissedIds]);

	if (!shouldShow || activeAnnouncements.length === 0) {
		return null;
	}

	const handleCopy = async (announcement) => {
		const plainText = stripHtml(announcement.message?.message_html || "");
		if (!plainText.trim()) {
			setCopyNotice("No message template available to copy.");
			return;
		}

		try {
			await navigator.clipboard.writeText(plainText);
			setCopyNotice("Message copied to clipboard.");
		} catch (error) {
			console.error("Copy failed:", error);
			setCopyNotice("Unable to copy message.");
		}
	};

	return (
		<Box sx={{ mb: 2 }}>
			{activeAnnouncements.map((announcement) => {
				const bodyText =
					stripHtml(announcement.message?.message_html || "") ||
					"A message template has not been published for this visit type yet.";

				return (
					<Collapse in key={announcement.visitType.id}>
						<Alert
							severity="info"
							sx={{
								mb: 1,
								alignItems: "flex-start",
								"& .MuiAlert-message": { width: "100%", p: 0 },
							}}>
							<Box sx={{ width: "100%" }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "flex-start",
										justifyContent: "space-between",
										gap: 1,
									}}>
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
											Service window active: {announcement.visitType.name}
										</Typography>
										<Typography variant="body2">{bodyText}</Typography>
									</Box>
									<IconButton
										size="small"
										color="inherit"
										sx={{ mt: -0.5, flexShrink: 0 }}
										onClick={() =>
											setDismissedIds((prev) => [...prev, announcement.visitType.id])
										}
										aria-label="Dismiss announcement">
										<CloseIcon fontSize="small" />
									</IconButton>
								</Box>
								<Box
									sx={{
										display: "flex",
										flexWrap: "wrap",
										gap: 1,
										mt: 1.5,
									}}>
									<Button
										size="small"
										color="inherit"
										variant="outlined"
										startIcon={<ContentCopyIcon />}
										onClick={() => handleCopy(announcement)}>
										Copy
									</Button>
									<Button
										size="small"
										color="inherit"
										variant="outlined"
										startIcon={<OpenInNewIcon />}
										onClick={() => {
											setSelectedVisitType(announcement.visitType);
											setModalOpen(true);
										}}>
										View
									</Button>
								</Box>
							</Box>
						</Alert>
					</Collapse>
				);
			})}

			{copyNotice && (
				<Alert severity="success" sx={{ mb: 1 }} onClose={() => setCopyNotice("")}>
					{copyNotice}
				</Alert>
			)}

			<VisitTypeMessageModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				visitType={selectedVisitType}
				message={
					selectedVisitType ? messagesByTypeId[selectedVisitType.id] : null
				}
				isAdmin={false}
			/>
		</Box>
	);
};

export default ServiceWindowAnnouncement;
